import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
  Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import tenantInvitationService, {
  type TenantInvitationPreview,
} from '../services/tenantInvitationService';
import { getApiErrorInfo } from '../services/api';
import { track } from '../services/analytics';
import { useUserContext } from '../hooks/useUserContext';
import { config } from '../config';
import CpaxLogo from '../components/CpaxLogo';
import {
  AuthPageLayout,
  LeftBrandContainer,
  LeftBrandContent,
  RightContentArea,
} from '../components/auth/SharedAuthComponents';

const DEFAULT_TENANT_ID = config.defaultTenantId;

const ROLE_LABELS: Record<string, string> = {
  user: 'Member',
  'tenant-admin': 'Administrator',
  admin: 'Administrator',
  'system-admin': 'System Administrator',
};

function formatRole(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

export default function AcceptTenantInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, isLoaded, isSignedIn, backendProfile, loading: profileLoading } = useUserContext();
  const isOnDefaultTenant = !backendProfile?.tenantId || backendProfile.tenantId === DEFAULT_TENANT_ID;

  const token = searchParams.get('token') ?? '';
  const tenantId = backendProfile?.tenantId ?? DEFAULT_TENANT_ID;

  const [preview, setPreview] = useState<TenantInvitationPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [revoked, setRevoked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const acceptedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded || profileLoading) return;

    if (!isSignedIn) {
      const returnTo = `/accept-tenant-invite?token=${encodeURIComponent(token)}`;
      navigate(`/auth/login?redirect_url=${encodeURIComponent(returnTo)}`, { replace: true });
      return;
    }

    if (acceptedRef.current) return;

    if (!token) {
      setError('This invitation link is missing its token.');
      setLoading(false);
      return;
    }

    tenantInvitationService
      .preview(token, tenantId)
      .then((p) => setPreview(p))
      .catch((err) => {
        const status = err?.response?.status;
        const data = err?.response?.data;

        if (status === 410 && data?.code === 'revoked') {
          setRevoked(true);
          setError(data.message ?? 'This invitation has been superseded by a newer one. Check your email for the latest link.');
        } else {
          setError(
            data?.message ??
              'This invitation is invalid, expired, or belongs to a different user.'
          );
        }
      })
      .finally(() => setLoading(false));
  }, [isLoaded, profileLoading, isSignedIn, token, tenantId, navigate]);

  const handleAccept = async () => {
    // For users switching from an existing org, require confirmation first
    if (!isOnDefaultTenant && !confirming) {
      setConfirming(true);
      return;
    }

    if (!token) return;
    try {
      setAccepting(true);
      setError(null);
      await tenantInvitationService.accept(token, tenantId);
      acceptedRef.current = true;
      track('invite_accepted');
      setSuccess(preview?.tenantName ?? 'the organization');
      await refreshUser();
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to accept invitation.');
      setConfirming(false);
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    navigate('/app/assistant', { replace: true });
  };

  return (
    <AuthPageLayout>
      <LeftBrandContainer>
        <LeftBrandContent
          title="You've been invited"
          subtitle="Someone on your team is ready for you to join them on CPAx Academy."
        />
      </LeftBrandContainer>

      <RightContentArea>
        {/* Loading state */}
        {loading && (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Loading your invitation...
            </Typography>
          </Stack>
        )}

        {/* Success state */}
        {success && !loading && (
          <Stack alignItems="center" spacing={3} sx={{ textAlign: 'center', maxWidth: 400 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: '#fff' }} />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: 24, sm: 32 },
                fontWeight: 400,
                letterSpacing: '0.25px',
                color: 'primary.main',
              }}
            >
              Welcome to {success}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You've joined as {formatRole(preview?.role ?? 'user').toLowerCase()}. Your account has been updated.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/app/assistant', { replace: true })}
              sx={{ mt: 1, px: 5 }}
            >
              Get started
            </Button>
          </Stack>
        )}

        {/* Error state (no preview available) */}
        {error && !preview && !loading && !success && (
          <Stack alignItems="center" spacing={3} sx={{ textAlign: 'center', maxWidth: 420 }}>
            <Box sx={{ mb: 1 }}>
              <CpaxLogo height={36} />
            </Box>

            {revoked ? (
              <>
                <Alert severity="warning" sx={{ width: '100%', textAlign: 'left' }}>
                  A newer invitation has been sent to your email. Please use the link from the most recent email to accept the invitation.
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  If you can't find it, ask your administrator to resend the invitation.
                </Typography>
              </>
            ) : (
              <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={() => navigate('/app/assistant', { replace: true })}
            >
              Go to CPAx Academy
            </Button>
          </Stack>
        )}

        {/* Preview state (main invitation view) */}
        {preview && !success && !loading && (
          <Stack spacing={3} sx={{ width: '100%', maxWidth: 440 }}>
            {/* Tenant identity */}
            <Stack alignItems="center" spacing={2}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: 24, sm: 32 },
                  fontWeight: 400,
                  letterSpacing: '0.25px',
                  color: 'primary.main',
                  textAlign: 'center',
                }}
              >
                Join {preview.tenantName}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                <strong>{preview.invitedByName || 'An administrator'}</strong> invited you to join as {formatRole(preview.role).toLowerCase()}.
              </Typography>
            </Stack>

            {/* Error from accept attempt */}
            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}

            {/* Consequence disclosure */}
            {isOnDefaultTenant ? (
              <Alert severity="info" icon={false} sx={{ bgcolor: 'tint.primarySoft', color: 'text.primary', '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2">
                  Your account will be linked to <strong>{preview.tenantName}</strong>, giving you access to their courses and resources.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="warning">
                <Typography variant="body2">
                  This will move your account from <strong>{preview.currentTenantName}</strong> to <strong>{preview.tenantName}</strong>. You will lose access to courses and data tied to your current organization.
                </Typography>
              </Alert>
            )}

            {/* Org-switch confirmation */}
            <Collapse in={confirming}>
              <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Are you sure you want to leave {preview.currentTenantName}?
                </Typography>
                <Typography variant="body2">
                  This cannot be undone. Your administrator would need to re-invite you to return.
                </Typography>
              </Alert>
            </Collapse>

            {/* Actions */}
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={handleAccept}
                disabled={accepting}
                sx={{ flex: 1 }}
              >
                {accepting
                  ? 'Joining...'
                  : confirming
                    ? 'Confirm and join'
                    : 'Accept invitation'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={confirming ? () => setConfirming(false) : handleDecline}
                disabled={accepting}
                sx={{ flex: 1 }}
              >
                {confirming ? 'Go back' : 'Decline for now'}
              </Button>
            </Stack>

            {/* Expiration */}
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              This invitation expires on{' '}
              {new Date(preview.expiresAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
              .
            </Typography>
          </Stack>
        )}
      </RightContentArea>
    </AuthPageLayout>
  );
}
